import { useMemo } from "react";
import type { WorkflowNode } from "@/types/aiToolkit";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AGENTS } from "@/data/aiToolkit";
import { NODE_META } from "./nodeMeta";

/**
 * Config panel — surfaces only the data.* fields the engine already executes
 * (see NODE_META.fields). Writes back a shallow-merged data object; the canvas
 * owns persistence via onSave (same contract as the legacy WorkflowBuilder).
 */
export function NodeConfigPanel({
  node,
  onChange,
  onDelete,
  onClose,
  runOutput,
}: {
  node: WorkflowNode;
  onChange: (nodeId: string, dataPatch: Partial<WorkflowNode["data"]>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
  /** 4A.8: last run output snapshot for this node (read-only display) */
  runOutput?: unknown;
}) {
  const meta = NODE_META[node.type];
  const fields = meta?.fields ?? ["label"];
  const agents = useMemo(() => AGENTS ?? [], []);

  const set = (patch: Partial<WorkflowNode["data"]>) => onChange(node.id, patch);
  const d = node.data;

  return (
    <div
      style={{
        position: "absolute", top: 0, right: 0, height: "100%", width: 300,
        background: "var(--card, #fff)", color: "var(--card-foreground, #0f172a)",
        borderLeft: "1px solid var(--border, #e2e8f0)", padding: 14, overflowY: "auto", zIndex: 5,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <strong>{meta?.label ?? node.type}</strong>
        <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
      </div>

      {meta && !meta.executes && (
        <p style={{ fontSize: 12, color: "#b45309", marginBottom: 10 }}>
          This node type is not yet executed by the engine (planned in 4A.3/4A.4). You can place and
          configure it, but a run will skip it.
        </p>
      )}

      <Label>Label</Label>
      <Input value={d.label ?? ""} onChange={(e) => set({ label: e.target.value })} />

      {fields.includes("description") && (
        <>
          <Label>Description</Label>
          <Input value={d.description ?? ""} onChange={(e) => set({ description: e.target.value })} />
        </>
      )}

      {fields.includes("agentId") && (
        <>
          <Label>Agent</Label>
          <select
            value={d.agentId ?? ""}
            onChange={(e) => set({ agentId: e.target.value })}
            style={selectStyle}
          >
            <option value="">— select agent —</option>
            {agents.map((a: { id: string; name: string }) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <p style={hint}>Resolved from ai_agents; runs through the AI proxy.</p>
        </>
      )}

      {fields.includes("workerProfileId") && (
        <>
          <Label>Worker profile ID</Label>
          <Input value={d.workerProfileId ?? ""} onChange={(e) => set({ workerProfileId: e.target.value })} />
        </>
      )}

      {fields.includes("skillIds") && (
        <>
          <Label>Skill IDs (comma-separated)</Label>
          <Input
            value={(d.skillIds ?? []).join(", ")}
            onChange={(e) => set({ skillIds: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </>
      )}

      {fields.includes("promptTemplate") && (
        <>
          <Label>{node.type === "crew" ? "Goal (what the crew must achieve)" : "Prompt template"}</Label>
          <textarea value={d.promptTemplate ?? ""} onChange={(e) => set({ promptTemplate: e.target.value })}
            style={{ ...selectStyle, minHeight: 90, fontFamily: "inherit" }} />
        </>
      )}

      {node.type === "crew" && (
        <p style={{ ...hint, marginTop: 8 }}>
          Connect worker agent nodes to this crew's <strong>bottom "worker" handle</strong>. The manager
          (Agent above, optional) delegates to them until the goal is met. Use the right handle for what
          runs after the crew finishes.
        </p>
      )}
      {fields.includes("crewMaxRounds") && (
        <>
          <Label>Max delegation rounds</Label>
          <Input type="number" min="1" max="12" value={d.crewMaxRounds ?? ""} placeholder="auto"
            onChange={(e) => set({ crewMaxRounds: e.target.value ? Number(e.target.value) : undefined })} />
          <p style={hint}>Bounded (cap 12). Blank = auto (scales with worker count).</p>
        </>
      )}

      {fields.includes("toolId") && (
        <>
          <Label>Tool ID</Label>
          <Input value={d.toolId ?? ""} onChange={(e) => set({ toolId: e.target.value })} />
        </>
      )}
      {fields.includes("toolOperation") && (
        <>
          <Label>Tool operation</Label>
          <Input value={d.toolOperation ?? ""} onChange={(e) => set({ toolOperation: e.target.value })} />
        </>
      )}

      {fields.includes("condition") && (
        <>
          <Label>Condition expression</Label>
          <Input value={d.condition ?? ""} onChange={(e) => set({ condition: e.target.value })} />
          <p style={hint}>Emits <code>true</code>/<code>false</code> handles.</p>
        </>
      )}

      {fields.includes("confidenceThreshold") && (
        <>
          <Label>Confidence threshold (0–1)</Label>
          <Input type="number" step="0.05" min="0" max="1" value={d.confidenceThreshold ?? 0.7}
            onChange={(e) => set({ confidenceThreshold: Number(e.target.value) })} />
          <p style={hint}>Emits <code>confidence_pass</code>/<code>confidence_fail</code>.</p>
        </>
      )}
      {fields.includes("qualityThreshold") && (
        <>
          <Label>Quality threshold (0–1)</Label>
          <Input type="number" step="0.05" min="0" max="1" value={d.qualityThreshold ?? 0.7}
            onChange={(e) => set({ qualityThreshold: Number(e.target.value) })} />
        </>
      )}

      {fields.includes("mergeStrategy") && (
        <>
          <Label>Merge strategy</Label>
          <select value={d.mergeStrategy ?? "all"} onChange={(e) => set({ mergeStrategy: e.target.value as "all" | "any" | "first" })} style={selectStyle}>
            <option value="all">all</option>
            <option value="any">any</option>
            <option value="first">first</option>
          </select>
        </>
      )}

      {fields.includes("knowledgeSource") && (
        <>
          <Label>Knowledge source</Label>
          <select value={d.knowledgeSource ?? "kb"} onChange={(e) => set({ knowledgeSource: e.target.value as "kb" | "thread" | "integration" | "space" | "trigger" })} style={selectStyle}>
            {["kb", "thread", "integration", "space", "trigger"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )}

      {fields.includes("loopCount") && (
        <>
          <Label>Loop count</Label>
          <Input type="number" min="1" value={d.loopCount ?? 1} onChange={(e) => set({ loopCount: Number(e.target.value) })} />
        </>
      )}
      {fields.includes("delayMs") && (
        <>
          <Label>Delay (ms)</Label>
          <Input type="number" min="0" value={d.delayMs ?? 0} onChange={(e) => set({ delayMs: Number(e.target.value) })} />
        </>
      )}

      {fields.includes("httpConfig") && (
        <>
          <Label>HTTP URL (https only)</Label>
          <Input value={d.httpConfig?.url ?? ""} onChange={(e) => set({ httpConfig: { ...(d.httpConfig ?? { method: "GET", url: "" }), url: e.target.value } })} />
          <Label>Method</Label>
          <select value={d.httpConfig?.method ?? "GET"} onChange={(e) => set({ httpConfig: { ...(d.httpConfig ?? { url: "" }), method: e.target.value } })} style={selectStyle}>
            {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Label>Body (non-GET)</Label>
          <textarea
            value={d.httpConfig?.body ?? ""}
            onChange={(e) => set({ httpConfig: { ...(d.httpConfig ?? { method: "POST", url: "" }), body: e.target.value } })}
            style={{ ...selectStyle, minHeight: 70, fontFamily: "inherit" }}
            placeholder='{"q": "{{nodeOutputs.node-1.raw}}"}'
          />
          <p style={hint}>
            URL/body/headers support {"{{variables.x}}"} and {"{{nodeOutputs.<id>.raw}}"}.
            Runs in the browser — the target must allow CORS. Private hosts blocked.
          </p>
        </>
      )}

      {runOutput !== undefined && (
        <div style={{ marginTop: 16 }}>
          <Label>Last run output</Label>
          <pre style={{ fontSize: 11, maxHeight: 220, overflow: "auto", padding: 8, borderRadius: 6, border: "1px solid var(--border, #e2e8f0)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {typeof runOutput === "string" ? runOutput : JSON.stringify(runOutput, null, 2)}
          </pre>
        </div>
      )}

      <div style={{ marginTop: 20, borderTop: "1px solid var(--border, #e2e8f0)", paddingTop: 12 }}>
        <Button variant="destructive" size="sm" onClick={() => onDelete(node.id)} style={{ width: "100%" }}>
          Delete node
        </Button>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, margin: "10px 0 4px" }}>{children}</div>;
}
const selectStyle: React.CSSProperties = {
  width: "100%", padding: "6px 8px", borderRadius: 6,
  border: "1px solid var(--border, #e2e8f0)", background: "transparent", color: "inherit", fontSize: 13,
};
const hint: React.CSSProperties = { fontSize: 11, opacity: 0.6, marginTop: 3 };
